import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import Select from "react-select";

import { useToast } from "src/context/ToastContext";
import { CriteriaManifestationEntity } from "src/entities/CriteriaManifestationEntity";
import { CriteriaRiskEntity } from "src/entities/CriteriaRiskEntity";
import CriteriaManifestationService, { criteriaManifestationQueryKeys } from "src/services/CriteriaManifestationService";
import CriteriaRiskService, { criteriaRiskQueryKeys } from "src/services/CriteriaRiskService";
import { SelectOption, SelectOptions, SelectStyle } from "src/config/select";
import Navigation from "./Navigation";
import { clearCriteriaStepDraft, getCriteriaStepDraft, setCriteriaStepDraft } from "./draftStorage";

const criteriaManifestationService = new CriteriaManifestationService();
const criteriaRiskService = new CriteriaRiskService();

type RiskItem = {
	id?: number;
	title: string;
	description: string;
	manifestations: number[];
	originalTitle?: string;
	originalDescription?: string;
	originalManifestations?: number[];
};

type StepRisksDraft = {
	risks: RiskItem[];
	removedIds: number[];
};

type StepRisksProps = {
	criteriaId: number;
	backLabel: string;
	nextLabel: string;
	onBack: () => void;
	onComplete: () => void;
};

export default function StepRisks({ criteriaId, backLabel, nextLabel, onBack, onComplete }: StepRisksProps) {
	const { handleToast } = useToast();
	const draftRef = useRef(getCriteriaStepDraft<StepRisksDraft>(criteriaId, "risks"));
	const [risks, setRisks] = useState<RiskItem[]>(() => draftRef.current?.risks ?? []);
	const [removedIds, setRemovedIds] = useState<number[]>(() => draftRef.current?.removedIds ?? []);

	const criteriaIdStr = String(criteriaId);

	const { data: manifestationsData, isLoading: manifestationsLoading } = useQuery<CriteriaManifestationEntity[]>(
		criteriaManifestationQueryKeys.manifestations(criteriaIdStr),
		() => criteriaManifestationService.getManifestations(criteriaId).then(response => response.data.manifestations),
		{ refetchOnWindowFocus: false }
	);

	const { data: risksData, isLoading: risksLoading } = useQuery<CriteriaRiskEntity[]>(
		criteriaRiskQueryKeys.risks(criteriaIdStr),
		() => criteriaRiskService.getRisks(criteriaId).then(response => response.data.risks),
		{ refetchOnWindowFocus: false }
	);

	useEffect(() => {
		if (!draftRef.current && risksData) {
			setRisks(risksData.map(risk => {
				const manifestationIds = risk.manifestations.map(manifestation => manifestation.id);
				return {
					id: risk.id,
					title: risk.title,
					description: risk.description,
					manifestations: manifestationIds,
					originalTitle: risk.title,
					originalDescription: risk.description,
					originalManifestations: manifestationIds,
				};
			}));
		}
	}, [risksData]);

	const manifestationOptions = manifestationsData?.map(manifestation => ({ label: manifestation.title, value: manifestation.id })) ?? [];
	const isLoading = manifestationsLoading || risksLoading;

	async function saveDeleted() {
		await Promise.all(removedIds.map(riskId => criteriaRiskService.deleteRiskRelation(criteriaId, riskId)));
	}

	async function saveUpdated(updated: RiskItem[]) {
		await Promise.all(updated.map(risk => criteriaRiskService.putRisk({ id: risk.id!, title: risk.title, description: risk.description })));
	}

	async function syncManifestations(existing: RiskItem[]) {
		const calls: Promise<unknown>[] = [];

		for (const risk of existing) {
			const original = risk.originalManifestations ?? [];
			const added = risk.manifestations.filter(id => !original.includes(id));
			const removed = original.filter(id => !risk.manifestations.includes(id));

			if (added.length > 0) {
				calls.push(criteriaRiskService.postRiskManifestationRelations({ riskId: risk.id!, manifestations: added }));
			}

			for (const manifestationId of removed) {
				calls.push(criteriaRiskService.deleteRiskManifestationRelation(risk.id!, manifestationId));
			}
		}

		await Promise.all(calls);
	}

	async function saveCreated(created: RiskItem[]) {
		if(created.length > 0) {
			const responses = await Promise.all(
				created.map(risk => criteriaRiskService.postRisk({ title: risk.title, description: risk.description }))
			);

			const newIds = responses.map(response => response.data.id);
			const calls: Promise<unknown>[] = [
				criteriaRiskService.postRiskRelations({ criteriaId, risks: newIds })
			];

			created.forEach((risk, idx) => {
				if (risk.manifestations.length > 0) {
					calls.push(criteriaRiskService.postRiskManifestationRelations({ riskId: newIds[idx], manifestations: risk.manifestations }));
				}
			});

			await Promise.all(calls);
		}
	}

	const mutation = useMutation(
		async () => {
			const created = risks.filter(risk => !risk.id && risk.title.trim());
			const updated = risks.filter(risk => risk.id && (risk.title !== risk.originalTitle || risk.description !== risk.originalDescription));
			const existing = risks.filter(risk => risk.id);

			await Promise.all([
				saveDeleted(),
				saveUpdated(updated),
				syncManifestations(existing),
				saveCreated(created),
			]);
		},
		{
			onSuccess: () => {
				clearCriteriaStepDraft(criteriaId, "risks");
				onComplete();
			},
			onError: (err: AxiosError) => {
				handleToast("Critério", `Problema na operação: ${err.message}`, 5000, "danger");
			},
		}
	);

	function handleAdd() {
		setRisks(prev => [...prev, { title: "", description: "", manifestations: [] }]);
	}

	function handleTitleChange(index: number, value: string) {
		setRisks(prev => prev.map((risk, idx) => idx === index ? { ...risk, title: value } : risk));
	}

	function handleDescriptionChange(index: number, value: string) {
		setRisks(prev => prev.map((risk, idx) => idx === index ? { ...risk, description: value } : risk));
	}

	function handleManifestationsChange(index: number, value: readonly SelectOption[] | null) {
		setRisks(prev => prev.map((risk, idx) => idx === index ? { ...risk, manifestations: value ? value.map(option => option.value) : [] } : risk));
	}

	function handleRemove(index: number) {
		const risk = risks[index];

		if (risk.id) {
			setRemovedIds(prev => [...prev, risk.id!]);
		}
		
		setRisks(prev => prev.filter((_, idx) => idx !== index));
	}

	function handleBack() {
		setCriteriaStepDraft(criteriaId, "risks", { risks, removedIds });
		onBack();
	}

	return (
		<>
			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Riscos
				</Card.Header>
				<Card.Body>
					{isLoading ? (
						Array.from({ length: 3 }, (_, skeletonIndex) => (
							<div key={skeletonIndex} className="mb-3">
								<Row className="mb-2 gy-2">
									<Col md={6}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col md={6}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
								</Row>
								<Row className="align-items-end gy-2">
									<Col xs={12} lg>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col xs={12} lg="auto" className="d-flex align-items-end">
										<div className="skeleton skeleton-button" style={{ height: 38, width: 44 }} />
									</Col>
								</Row>
							</div>
						))
					) : risks.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						risks.map((risk, index) => (
							<div
								key={index}
								className={index < risks.length - 1 ? "border-bottom pb-4 mb-3" : "mb-3"}
							>
								<Row className="mb-2 gy-2">
									<Col md={6}>
										<Form.Label className="text-muted"><small>Título *</small></Form.Label>
										<Form.Control
											type="text"
											placeholder="Informe aqui"
											value={risk.title}
											onChange={(e) => handleTitleChange(index, e.target.value)}
										/>
									</Col>
									<Col md={6}>
										<Form.Label className="text-muted"><small>Descrição *</small></Form.Label>
										<Form.Control
											type="text"
											placeholder="Informe aqui"
											value={risk.description}
											onChange={(e) => handleDescriptionChange(index, e.target.value)}
										/>
									</Col>
								</Row>
								<Row className="align-items-end gy-2">
									<Col xs={12} lg>
										<Form.Label className="text-muted"><small>Manifestações</small></Form.Label>
										<Select
											placeholder="Selecione"
											isMulti
											options={manifestationOptions}
											value={manifestationOptions.filter(opt => risk.manifestations.includes(opt.value))}
											onChange={(value) => handleManifestationsChange(index, value)}
											styles={SelectStyle}
											{...SelectOptions}
										/>
									</Col>
									<Col xs={12} lg="auto" className="d-flex align-items-end">
										<Button variant="light" className="rounded-3 shadow-sm" type="button" onClick={() => handleRemove(index)}>
											<Icon path={mdiTrashCanOutline} size={1} className="text-danger" />
										</Button>
									</Col>
								</Row>
							</div>
						))
					)}
				</Card.Body>
				<Card.Footer className="bg-white">
					<Button
						variant="light"
						className="d-flex rounded-3 shadow-sm"
						type="button"
						onClick={handleAdd}
						disabled={isLoading}
					>
						<Icon path={mdiPlus} size={1} /> Adicionar Risco
					</Button>
				</Card.Footer>
			</Card>
			<Navigation
				backLabel={backLabel}
				nextLabel={nextLabel}
				saving={mutation.isLoading}
				onBack={handleBack}
				onNext={() => mutation.mutate()}
			/>
		</>
	);
}
